import AddIcon from '@mui/icons-material/Add'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment } from 'react'
import { useParams } from 'react-router-dom'

import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { generatePhoneFromString } from 'components-ui/src/shared'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { Hooks, Types } from 'service-api'
import { getLabelFromContactType } from './schema'

interface TContactCardProps {
  contact: Types.AssociatedContactClientDto
}

export const ContactCard = ({ contact }: TContactCardProps) => {
  const { navigateRoute } = useRoutes()
  const onClickHandler = () => navigateRoute('profileContactsEdit', { contactId: contact.id })

  return (
    <Paper key={contact.id} elevation={1} sx={{ p: 2 }}>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">{contact.displayName}</Typography>
          <Button onClick={onClickHandler}>Edit</Button>
        </Stack>
        <Typography variant="body1">{getLabelFromContactType(contact.type || 'OTHER')}</Typography>
        <Typography variant="body1">{generatePhoneFromString(contact.phone ?? undefined)}</Typography>
        <Typography variant="body1" sx={{ maxWidth: '80%' }}>
          {contact.description}
        </Typography>
      </Stack>
    </Paper>
  )
}

export const ProfileContactsList = () => {
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const { navigateRoute } = useRoutes()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  const contacts = Hooks.useClientLocationAssociatedContacts(locationName, sessionCtx.state.user?.lupId || '')

  const onAddPetHandler = () => navigateRoute('profileContactsAdd', {})

  if (contacts.isSuccess && contacts.data.results) {
    contacts.data.results.sort((a, b) => a.displayName?.localeCompare(b.displayName || '') ?? -1)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" pl={isBelowMD ? 2 : 0}>
        {t('profile.contacts.title')}
      </Typography>
      <Spacer height={2} />

      {contacts.isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : !contacts.data?.results?.length ? (
        <Typography variant="body1" pl={isBelowMD ? 2 : 0}>
          {t('profile.contacts.noContacts')}
        </Typography>
      ) : (
        <Fragment>
          <Stack direction="column" spacing={2}>
            {contacts.data?.results?.map((contact) => (
              <ContactCard key={contact.id} contact={contact} />
            ))}
          </Stack>
        </Fragment>
      )}
      <Spacer height={2} />
      <Box pl={isBelowMD ? 2 : 0}>
        <Button variant="text" startIcon={<AddIcon />} onClick={onAddPetHandler}>
          {t('profile.contacts.addContact')}
        </Button>
      </Box>
    </Box>
  )
}
